// ahri_v5 — domain/solrDocs.js

import { AHRI_COMPONENT_SLOTS, parseComponentOptions, hasComponentChoices } from "../ahriSystemSelection.js";
import { normalizeWarehouseCode } from "./availability.js";
import { normalizeFeaturedGroupTag, isAllWarehouseTag, isFeaturedForWarehouse, compareSystemsForDisplay } from "./featured.js";
import { isTruthyLike } from "./ahriRecords.js";

const VARIANT_WAREHOUSE_FLAG_RE = /^(buildable|complete)_at_(.+)$/;
const VARIANT_QTY_FIELD_RE = /^qty_(indoor|outdoor|furnace)_at_(.+)$/;

function scalarFromDoc(value) {
  return Array.isArray(value) ? value[0] : value;
}

export function normalizeSolrRowField(field) {
  const raw = String(field || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower.startsWith("rows_") || lower.startsWith("prodattr_")) return lower;
  return `rows_${lower.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "")}`;
}
export function toBoolList(value) {
  const list = Array.isArray(value) ? value : value !== undefined && value !== null ? [value] : [];
  return list.map((entry) => String(entry).toLowerCase());
}

export function getValueFromDoc(doc, field) {
  const raw = doc?.[field];
  if (Array.isArray(raw)) return raw[0] || "";
  return raw || "";
}

// rows_programname -> ahridirectory.org ProgramId, used to build the details URL
// client-side. Unmapped/missing program names fall back to the backend lookup.
const AHRI_DIRECTORY_DETAILS_URL_BASE = "https://www.ahridirectory.org/details";
const AHRI_DIRECTORY_PROGRAM_IDS = { AC: 101, ASHP: 99, ULE: 3 };

// Builds the ahridirectory.org details URL from the system's rows_programname
// (see AHRI_DIRECTORY_PROGRAM_IDS). Returns "" when the program isn't mapped.
export function getAhriDirectoryUrlFromSystem(ahriId, system) {
  const normalizedId = String(ahriId || "").trim();
  const programName = String(getValueFromDoc(system?.rawDoc, "rows_programname") || "").trim().toUpperCase();
  const programId = AHRI_DIRECTORY_PROGRAM_IDS[programName];
  if (!normalizedId || !programId) return "";
  return `${AHRI_DIRECTORY_DETAILS_URL_BASE}/${programId}/${encodeURIComponent(normalizedId)}`;
}

export function getValuesFromDoc(doc, field) {
  const raw = doc?.[field];
  const source = Array.isArray(raw) ? raw : raw !== undefined && raw !== null ? [raw] : [];
  return source
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);
}

export function getNumericFromDoc(doc, field) {
  const raw = doc?.[field];
  const value = Array.isArray(raw) ? raw[0] : raw;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export function parsePayloadJsonDoc(doc) {
  const payloadRaw = Array.isArray(doc?.payload_json) ? doc.payload_json[0] : doc?.payload_json;
  if (!payloadRaw || typeof payloadRaw !== "string") return null;
  try {
    const parsed = JSON.parse(payloadRaw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

// Story 3.1 (AD-7): when the account has cut over to variant-based display
// (or a restricted customer forced it on, Story 2.2), each result doc is the
// primary variant of a Solr-grouped AHRI system, carrying its sibling
// eligible combinations under `combinations` (raw variant docs, snake_case
// fields -- see _build_variant_solr_doc in backend/api/ahri.py) and the true
// total under `combinations_total`. This is a completely different doc
// shape from the legacy rows_* cert-level doc -- doc_type distinguishes them.
export function parseVariantCombination(raw) {
  if (!raw || typeof raw !== "object") return null;
  // Variant docs never carry a literal `availability_json` field -- Solr
  // only stores what _build_variant_solr_doc flattens onto the doc:
  // buildable_at_{wh}/complete_at_{wh} booleans and qty_{slot}_at_{wh}
  // numbers, one field per warehouse (and per slot for qty).
  const buildableWarehouses = [];
  const completeWarehouses = [];
  const qtyByWarehouse = {};
  for (const key of Object.keys(raw)) {
    const flagMatch = key.match(VARIANT_WAREHOUSE_FLAG_RE);
    if (flagMatch) {
      const [, kind, wh] = flagMatch;
      if (isTruthyLike(scalarFromDoc(raw[key]))) {
        (kind === "buildable" ? buildableWarehouses : completeWarehouses).push(wh);
      }
      continue;
    }
    const qtyMatch = key.match(VARIANT_QTY_FIELD_RE);
    if (qtyMatch) {
      const [, slot, wh] = qtyMatch;
      const num = Number(scalarFromDoc(raw[key]));
      if (Number.isFinite(num)) {
        qtyByWarehouse[wh] = { ...(qtyByWarehouse[wh] || {}), [slot]: num };
      }
    }
  }
  return {
    indoorProductCode: String(raw.indoor_product_code || ""),
    outdoorProductCode: String(raw.outdoor_product_code || ""),
    furnaceProductCode: String(raw.furnace_product_code || ""),
    indoorManufacturer: String(raw.indoor_manufacturer || ""),
    outdoorManufacturer: String(raw.outdoor_manufacturer || ""),
    indoorBrand: String(raw.indoor_brand || ""),
    outdoorBrand: String(raw.outdoor_brand || ""),
    // Denormalized product info (migration 177) -- present directly on the
    // search response doc, so a card can render name/image/description
    // immediately without the separate /products/lookup round trip.
    indoorName: String(scalarFromDoc(raw.indoor_name) || ""),
    outdoorName: String(scalarFromDoc(raw.outdoor_name) || ""),
    furnaceName: String(scalarFromDoc(raw.furnace_name) || ""),
    indoorImageUrl: String(scalarFromDoc(raw.indoor_image_url) || ""),
    outdoorImageUrl: String(scalarFromDoc(raw.outdoor_image_url) || ""),
    furnaceImageUrl: String(scalarFromDoc(raw.furnace_image_url) || ""),
    indoorDescription: String(scalarFromDoc(raw.indoor_description) || ""),
    outdoorDescription: String(scalarFromDoc(raw.outdoor_description) || ""),
    furnaceDescription: String(scalarFromDoc(raw.furnace_description) || ""),
    isCompleteMatch: !!raw.is_complete_match,
    source: String(raw.source || ""),
    matchScore: typeof raw.match_score === "number" ? raw.match_score : null,
    buildableWarehouses,
    // "Completely available at this warehouse": every slot the certificate
    // requires was matched AND is in stock there (not just this combo's own
    // present codes) -- use this, not buildableWarehouses, for dots/badges.
    completeWarehouses,
    completeAnyWarehouse: isTruthyLike(scalarFromDoc(raw.complete_any_warehouse)),
    qtyByWarehouse,
  };
}

/** One slot's embedded name/image/description from a parsed variant
 * combination, in the same `{ name, image_url, description }` shape the
 * live /products/lookup cache uses -- lets callers fall back to this
 * immediately-available data while that slower cache populates. */
export function getEmbeddedSlotProductInfo(combination, slotType) {
  if (!combination) return null;
  const name = combination[`${slotType}Name`];
  const imageUrl = combination[`${slotType}ImageUrl`];
  const description = combination[`${slotType}Description`];
  if (!name && !imageUrl && !description) return null;
  return { name, image_url: imageUrl, description };
}

/** Dot class + display qty for a parsed variant combination at one warehouse,
 * derived entirely from data already on the solr_search doc -- no live
 * products/lookup call needed for this card-level indicator. */
export function getVariantCombinationAvailability(combination, selectedWarehouse) {
  const wh = normalizeWarehouseCode(selectedWarehouse);
  if (!wh) return { dotClass: "unknown", qty: null };
  const isComplete = (combination?.completeWarehouses || []).includes(wh);
  const qty = combination?.qtyByWarehouse?.[wh] || null;
  return { dotClass: isComplete ? "available" : "unavailable", qty };
}

// Per-slot distinct product codes across every eligible combination (primary
// combo's code first), in the same `{ field, label, model, codes }` shape
// `parseComponentOptions` produces for legacy docs -- lets the existing
// per-slot picker UI (and componentSelections/buildConfiguredSystem) work for
// variant-mode systems unchanged. Each slot is offered independently here;
// the brand shown per slot comes from codeToBrand below so the summary line
// still reflects whichever code is actually selected per slot.
function buildVariantComponentOptions(combinations) {
  const result = {};
  const codeToBrand = {};
  for (const slot of AHRI_COMPONENT_SLOTS) {
    const codeField = `${slot.type}ProductCode`;
    const brandField = `${slot.type}Brand`;
    const seen = new Set();
    const codes = [];
    for (const combo of combinations) {
      const code = String(combo?.[codeField] || "").trim();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      codes.push(code);
      if (combo?.[brandField] && !(code in codeToBrand)) {
        codeToBrand[code] = combo[brandField];
      }
    }
    result[slot.type] = { type: slot.type, field: slot.field, label: slot.label, model: "", codes };
  }
  return { componentOptions: result, codeToBrand };
}

// prodattr_s_seer2/hspf2/eer2 (migration 172) is the one reliable field per
// rating -- the backend tries every historical row_json spelling and
// normalizes onto this field for both ahri_system and ahri_system_variant
// docs. rows_<ratingKey> is the one structural fallback kept for docs
// indexed before that backfill lands; any other alternate spelling comes
// entirely from whatever fields are configured for this filter key in
// System Shop Filters (admin config) -- nothing else is hardcoded.
function getPerformanceRatingFromDoc(doc, ratingKey, filterConfig) {
  const primary = getValueFromDoc(doc, `prodattr_s_${ratingKey}`);
  if (primary) return primary;
  const structuralFallback = getValueFromDoc(doc, `rows_${ratingKey}`);
  if (structuralFallback) return structuralFallback;
  const filter = (Array.isArray(filterConfig) ? filterConfig : []).find(
    (entry) => String(entry?.key || "").trim().toLowerCase() === ratingKey
  );
  for (const field of Array.isArray(filter?.fields) ? filter.fields : []) {
    if (field === `prodattr_s_${ratingKey}` || field === `rows_${ratingKey}`) continue;
    const value = getValueFromDoc(doc, field);
    if (value) return value;
  }
  return "";
}

export function parseSystemDoc(doc, filterConfig = []) {
  const isVariantDoc = String(doc?.doc_type || "") === "ahri_system_variant";
  if (isVariantDoc) {
    const combinations = (Array.isArray(doc?.combinations) ? doc.combinations : [doc])
      .map(parseVariantCombination)
      .filter(Boolean);
    const primary = combinations[0] || parseVariantCombination(doc) || {};
    const { componentOptions, codeToBrand } = buildVariantComponentOptions(combinations);
    // Variant docs carry the same flattened rows_featured_* union fields as
    // cert docs (_build_variant_solr_doc), but no payload_json -- so there's
    // no per-group featuredMemberships detail here, only the flattened lists.
    const variantFeaturedGroupNames = getValuesFromDoc(doc, "rows_featured_group_names");
    const variantFeaturedGroupKeys = getValuesFromDoc(doc, "rows_featured_group_keys");
    const variantFeaturedGroupTypes = getValuesFromDoc(doc, "rows_featured_group_types");
    const variantFeaturedBranchCodes = getValuesFromDoc(doc, "rows_featured_branch_codes");
    return {
      id: String(doc?.ahri_id || ""),
      rawDoc: doc,
      variantMode: true,
      combinations,
      combinationsTotal: Number(doc?.combinations_total || combinations.length || 0),
      componentOptions,
      hasComponentChoices: hasComponentChoices(componentOptions),
      codeToBrand,
      outdoorBrand: primary.outdoorBrand,
      indoorBrand: primary.indoorBrand,
      outdoorModel: primary.outdoorProductCode,
      indoorModel: primary.indoorProductCode,
      furnaceModel: primary.furnaceProductCode,
      outdoorProductCode: primary.outdoorProductCode,
      indoorProductCode: primary.indoorProductCode,
      furnaceProductCode: primary.furnaceProductCode,
      completeMatch: combinations.some((c) => c.isCompleteMatch),
      partialMatch: combinations.length > 0 && !combinations.some((c) => c.isCompleteMatch),
      seer2: getPerformanceRatingFromDoc(doc, "seer2", filterConfig),
      hspf2: getPerformanceRatingFromDoc(doc, "hspf2", filterConfig),
      eer2: getPerformanceRatingFromDoc(doc, "eer2", filterConfig),
      featuredGroupNames: variantFeaturedGroupNames,
      featuredGroupName: variantFeaturedGroupNames[0] || variantFeaturedGroupTypes[0] || "",
      featuredBranchCodes: variantFeaturedBranchCodes,
      featuredMemberships: [],
      validFeatured: variantFeaturedGroupKeys.length > 0,
    };
  }
  const payloadDoc = parsePayloadJsonDoc(doc);
  const payloadFeaturedGroupNames = Array.isArray(payloadDoc?.featured_group_names)
    ? payloadDoc.featured_group_names.map((entry) => String(entry || "").trim()).filter(Boolean)
    : [];
  const payloadFeaturedGroupKeys = Array.isArray(payloadDoc?.featured_group_keys)
    ? payloadDoc.featured_group_keys.map((entry) => String(entry || "").trim()).filter(Boolean)
    : [];
  const payloadFeaturedGroupTypes = Array.isArray(payloadDoc?.featured_group_types)
    ? payloadDoc.featured_group_types.map((entry) => String(entry || "").trim()).filter(Boolean)
    : [];
  // rows_featured_branch_codes is derived ONLY from each AHRI's own row-level
  // branch_codes within a featured group -- never from the group's own tags.
  const payloadFeaturedBranchCodes = Array.isArray(payloadDoc?.featured_branch_codes)
    ? payloadDoc.featured_branch_codes.map((entry) => String(entry || "").trim()).filter(Boolean)
    : [];
  const completeMatch = toBoolList(doc?.rows_ahri_complete_match).includes("true");
  const partialMatch = toBoolList(doc?.rows_ahri_partial_match).includes("true");
  const featuredGroupNames = getValuesFromDoc(doc, "rows_featured_group_names");
  const featuredGroupKeys = getValuesFromDoc(doc, "rows_featured_group_keys");
  const featuredGroupTypes = getValuesFromDoc(doc, "rows_featured_group_types");
  const featuredBranchCodes = getValuesFromDoc(doc, "rows_featured_branch_codes");
  const resolvedFeaturedGroupNames = featuredGroupNames.length ? featuredGroupNames : payloadFeaturedGroupNames;
  const resolvedFeaturedGroupKeys = featuredGroupKeys.length ? featuredGroupKeys : payloadFeaturedGroupKeys;
  const resolvedFeaturedGroupTypes = featuredGroupTypes.length ? featuredGroupTypes : payloadFeaturedGroupTypes;
  const resolvedFeaturedBranchCodes = featuredBranchCodes.length ? featuredBranchCodes : payloadFeaturedBranchCodes;
  // Per-group membership detail (group name + that group's own branch_codes)
  // is only available via payload_json -- rows_* fields are flattened unions
  // across all groups and can't tell which specific group(s) cover a given
  // branch. This lets the UI show every group name that actually applies to
  // the branch the shopper is browsing, not just one arbitrary name.
  const featuredMemberships = Array.isArray(payloadDoc?.featured_group_memberships)
    ? payloadDoc.featured_group_memberships
        .filter((entry) => entry && typeof entry === "object")
        .map((entry) => ({
          groupName: String(entry.group_name || entry.group_type || "").trim(),
          branchCodes: Array.isArray(entry.branch_codes)
            ? entry.branch_codes.map((code) => String(code || "").trim()).filter(Boolean)
            : [],
        }))
        .filter((entry) => entry.groupName)
    : [];
  // No separate boolean flag is indexed for "featured" -- a non-empty
  // group-keys list is what makes an AHRI featured at all.
  const validFeatured = resolvedFeaturedGroupKeys.length > 0;
  const featuredGroupName = resolvedFeaturedGroupNames[0] || resolvedFeaturedGroupTypes[0] || "";
  const outdoorModel = getValueFromDoc(doc, "rows_outdoormodelnumber");
  const indoorModel = getValueFromDoc(doc, "rows_indoormodelnumber");
  const furnaceModel = getValueFromDoc(doc, "rows_furnacemodelnumber");
  // All approved product codes per slot (primary first). The single
  // *ProductCode fields keep the primary default for backward compatibility;
  // componentOptions powers the per-slot picker when alternatives exist.
  const componentOptions = parseComponentOptions(doc, {
    outdoor: outdoorModel,
    indoor: indoorModel,
    furnace: furnaceModel,
  });
  return {
    id: getValueFromDoc(doc, "rows_referenceid") || String(doc?.id || "").split("_").slice(1).join("_"),
    rawDoc: doc,
    outdoorBrand: getValueFromDoc(doc, "rows_outdoorbrandname"),
    indoorBrand: getValueFromDoc(doc, "rows_indoorbrandname"),
    outdoorModel,
    indoorModel,
    furnaceModel,
    outdoorProductCode: componentOptions.outdoor.codes[0] || "",
    indoorProductCode: componentOptions.indoor.codes[0] || "",
    furnaceProductCode: componentOptions.furnace.codes[0] || "",
    componentOptions,
    hasComponentChoices: hasComponentChoices(componentOptions),
    seer2: getPerformanceRatingFromDoc(doc, "seer2", filterConfig),
    hspf2: getPerformanceRatingFromDoc(doc, "hspf2", filterConfig),
    eer2: getPerformanceRatingFromDoc(doc, "eer2", filterConfig),
    isEnergyStar: toBoolList(doc?.rows_isenergystar).includes("true"),
    completeMatch,
    partialMatch,
    featuredGroupNames: resolvedFeaturedGroupNames,
    featuredGroupName,
    featuredBranchCodes: resolvedFeaturedBranchCodes,
    featuredMemberships,
    validFeatured,
    variantMode: false,
    combinations: [],
    combinationsTotal: 0,
  };
}
export function collectProductCodesFromDocs(docs) {
  const codes = new Set();
  (Array.isArray(docs) ? docs : []).forEach((doc) => {
    if (!doc || typeof doc !== "object") return;
    const addCode = (code) => {
      const normalized = String(code || "").trim();
      if (normalized) codes.add(normalized);
    };
    if (String(doc?.doc_type || "") === "ahri_system_variant") {
      // Variant docs have no prodmap_* fields at all -- parseComponentOptions
      // would silently return empty code lists for every slot here, which
      // previously left fetchProductDetails with nothing to fetch and every
      // variant-mode card rendering with no product name/image/price.
      (Array.isArray(doc?.combinations) ? doc.combinations : [doc])
        .map(parseVariantCombination)
        .filter(Boolean)
        .forEach((combo) => {
          addCode(combo.indoorProductCode);
          addCode(combo.outdoorProductCode);
          addCode(combo.furnaceProductCode);
        });
      return;
    }
    const options = parseComponentOptions(doc);
    Object.values(options).forEach((slot) => {
      (slot?.codes || []).forEach(addCode);
    });
  });
  return [...codes];
}

/** True when the shopper typed an AHRI ID or part/model number rather than a broad browse query. */
export function isDirectLookupQuery(query) {
  const text = String(query || "").trim();
  if (!text || text === "*:*") return false;
  if (/^\d{5,}$/.test(text)) return true;
  if (text.length >= 4 && !/\s/.test(text) && /^[A-Za-z0-9._/*+-]+$/.test(text)) {
    if (/[A-Za-z]/.test(text) && /\d/.test(text)) return true;
  }
  if (/^[A-Za-z0-9-]{4,}$/.test(text) && !/\s/.test(text)) return true;
  return false;
}

/** Map a slot availability dot class into a shopper-facing status pill ({ tone, text }). */
export function getSlotAvailabilityStatus(dotClass, warehouseCode, warehouseLabel = "") {
  const wh = normalizeWarehouseCode(warehouseCode);
  if (!wh || dotClass === "unknown") return null;
  const label = String(warehouseLabel || "").trim() || wh;
  if (dotClass === "available") return { tone: "available", text: `In stock at ${label}` };
  if (dotClass === "unavailable") return { tone: "unavailable", text: `Not at ${label}` };
  return null;
}

/** Read warehouse codes with positive availability from Solr dynamic fields on a system doc. */
export function getOtherWarehouseCodesFromDoc(doc, selectedWarehouse, fieldPrefix = "rowd_available_at_") {
  if (!doc || typeof doc !== "object") return [];
  const normalizedSelected = normalizeWarehouseCode(selectedWarehouse);
  const codes = [];
  // ahri_system_variant docs (Story 1.4/3.1) never carry rowd_available_at_*
  // fields -- availability per warehouse is instead a complete_at_{wh}
  // boolean (see parseVariantCombination above), so the legacy numeric
  // prefix scan below always finds zero fields and silently returns [].
  if (String(doc?.doc_type || "") === "ahri_system_variant") {
    Object.keys(doc).forEach((key) => {
      const match = key.match(VARIANT_WAREHOUSE_FLAG_RE);
      if (!match || match[1] !== "complete") return;
      const warehouseCode = normalizeWarehouseCode(match[2]);
      if (!warehouseCode || warehouseCode === normalizedSelected) return;
      if (isTruthyLike(scalarFromDoc(doc[key]))) {
        codes.push(warehouseCode);
      }
    });
    return [...new Set(codes)].sort();
  }
  const prefix = String(fieldPrefix || "");
  Object.keys(doc).forEach((key) => {
    if (!key.startsWith(prefix)) return;
    const warehouseCode = normalizeWarehouseCode(key.slice(prefix.length));
    if (!warehouseCode || warehouseCode === normalizedSelected) return;
    if (getNumericFromDoc(doc, key) > 0) {
      codes.push(warehouseCode);
    }
  });
  return [...new Set(codes)].sort();
}
